import React, { useState, useEffect } from 'react';
import Loader from '../Common/Loader';
import { getHomeworks, createHomework, updateHomework, deleteHomework, getHomeworkAnswers, reviewAnswer, getSections, getLessons, getCourses } from '../../services/adminService';
import HomeworkTree from './HomeworkTree';
import { uploadHomeworkImage, deleteHomeworkImage, uploadHomeworkFile } from '../../services/uploadService';
import Modal from '../Common/Modal';
import Button from '../Common/Button';
import RichEditor from '../Common/RichEditor';
import Dropdown from '../Common/Dropdown';
import { confirmDialog } from '../Common/confirm';
import { FileIcon, fileColor, fmtSize } from '../Common/fileKind';
import toast from 'react-hot-toast';

const HomeworkEditor = ({ courseId: fixedCourseId } = {}) => {
  const embedded = fixedCourseId != null; // внутри «внтрянки курса»
  const [homeworks, setHomeworks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sections, setSections] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [answerModalOpen, setAnswerModalOpen] = useState(false);
  const [editingHomework, setEditingHomework] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [selectedCourse, setSelectedCourse] = useState(fixedCourseId ?? null);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');

  const [quickAddMode, setQuickAddMode] = useState(false);

  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedLessons, setExpandedLessons] = useState({});
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachedSolutionFiles, setAttachedSolutionFiles] = useState([]);
  const [gridAnswers, setGridAnswers] = useState([]);
  // Меняем при каждом открытии/сбросе формы, чтобы пересоздать RichEditor
  // (он инициализирует содержимое только при монтировании — иначе условие
  // прошлого задания «залипает» в поле).
  const [formKey, setFormKey] = useState(0);

  const handleAddFile = async (file, type = 'homework') => {
    try {
      toast.loading('Загрузка файла...', { id: 'uploadFile' });
      const response = await uploadHomeworkFile(file);
      if (response.success) {
        const fileData = { url: response.data.url, name: response.data.originalName || file.name, size: file.size, type: file.type };
        if (type === 'homework') setAttachedFiles(prev => [...prev, fileData]);
        else setAttachedSolutionFiles(prev => [...prev, fileData]);
        toast.success('Файл добавлен', { id: 'uploadFile' });
      }
    } catch {
      toast.error('Ошибка загрузки файла', { id: 'uploadFile' });
    }
  };

  const [formData, setFormData] = useState({
    title: '', description: '', correctAnswer: '',
    answerType: 'single', gridRows: 3, gridCols: 2,
    textSolution: '', solutionImage: '', videoSolution: ''
  });

  const buildEmptyGrid = (rows, cols) => Array.from({ length: rows }, () => Array(cols).fill(''));
  const rebuildGrid = (rows, cols, old) => Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => old?.[r]?.[c] ?? '')
  );

  const [uploadedFile, setUploadedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (selectedCourse) loadSections(); }, [selectedCourse]);
  useEffect(() => { if (selectedSection) loadLessons(); }, [selectedSection]);

  const loadData = async () => {
    try {
      const [homeworksRes, coursesRes] = await Promise.all([getHomeworks(), getCourses()]);
      setHomeworks(homeworksRes.data || []);
      const cs = coursesRes.data || [];
      setCourses(cs);
      // Дефолтный курс: заданный (внутри курса) или первый существующий
      setSelectedCourse(prev => embedded ? fixedCourseId : (cs.some(c => c.id === prev) ? prev : (cs[0]?.id ?? null)));
    } catch (error) {
      console.error('Error loading homework data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async () => {
    try {
      const response = await getSections(selectedCourse);
      setSections(response.data || []);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error('Ошибка загрузки разделов');
    }
  };

  const loadLessons = async () => {
    try {
      const response = await getLessons(selectedSection);
      setLessons(response.data || []);
    } catch (error) {
      console.error('Error loading lessons:', error);
      toast.error('Ошибка загрузки уроков');
    }
  };

  const toggleCourse = (courseId) => setExpandedCourses(prev => ({ ...prev, [courseId]: !prev[courseId] }));
  const toggleSection = (sectionId) => setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  const toggleLesson = (lessonId) => setExpandedLessons(prev => ({ ...prev, [lessonId]: !prev[lessonId] }));

  const getNextHomeworkNumber = (lessonId) => homeworks.filter(hw => hw.lessonId === lessonId).length + 1;

  const handleAdd = () => {
    setEditingHomework(null);
    setFormData({ title: '', description: '', correctAnswer: '', answerType: 'single', gridRows: 3, gridCols: 2, textSolution: '', solutionImage: '', videoSolution: '' });
    setAttachedFiles([]); setAttachedSolutionFiles([]);
    setGridAnswers(buildEmptyGrid(3, 2));
    setUploadedFile(null); setImagePreview('');
    setSelectedLesson(''); setSelectedSection(''); setSelectedCourse(1);
    setQuickAddMode(false);
    setFormKey(k => k + 1);
    setModalOpen(true);
  };

  const handleQuickAdd = (lessonId, lesson) => {
    const nextNumber = getNextHomeworkNumber(lessonId);
    setEditingHomework(null);
    setFormData({ title: `№${nextNumber}`, description: '', correctAnswer: '', answerType: 'single', gridRows: 3, gridCols: 2, textSolution: '', solutionImage: '', videoSolution: '' });
    setGridAnswers(buildEmptyGrid(3, 2));
    setUploadedFile(null); setImagePreview('');
    setSelectedLesson(lessonId);
    setSelectedSection(lesson.sectionId);
    setSelectedCourse(lesson.courseId || 1);
    setQuickAddMode(true);
    setFormKey(k => k + 1);
    setModalOpen(true);
  };

  const handleEdit = (homework) => {
    setEditingHomework(homework);
    setSelectedLesson(homework.lessonId);
    if (homework.Lesson) {
      setSelectedSection(homework.Lesson.sectionId);
      setSelectedCourse(homework.Lesson.Section?.courseId || 1);
    }
    const aType = homework.answerType || 'single';
    const gRows = homework.gridRows || 3;
    const gCols = homework.gridCols || 2;
    setFormData({
      title: homework.title || '',
      description: homework.description || '',
      correctAnswer: aType === 'single' ? (homework.correctAnswer || '') : '',
      answerType: aType, gridRows: gRows, gridCols: gCols,
      textSolution: homework.textSolution || '',
      solutionImage: homework.solutionImage || '',
      videoSolution: homework.videoSolution || ''
    });
    if (aType === 'grid') {
      try {
        const parsed = JSON.parse(homework.correctAnswer || '[]');
        const flat = Array.isArray(parsed) ? parsed : [];
        const grid = Array.from({ length: gRows }, (_, r) =>
          Array.from({ length: gCols }, (_, c) => flat[r * gCols + c] ?? '')
        );
        setGridAnswers(grid);
      } catch { setGridAnswers(buildEmptyGrid(gRows, gCols)); }
    } else {
      setGridAnswers(buildEmptyGrid(gRows, gCols));
    }
    setAttachedFiles(homework.attachedFiles || []);
    setAttachedSolutionFiles(homework.attachedSolutionFiles || []);
    setUploadedFile(null); setImagePreview('');
    setQuickAddMode(false);
    setFormKey(k => k + 1);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (await confirmDialog({ title: 'Удалить задание?', message: 'Задание и ответы учеников на него будут удалены.', confirmText: 'Удалить' })) {
      try {
        await deleteHomework(id);
        toast.success('Задание удалено');
        loadData();
      } catch {
        toast.error('Ошибка при удалении');
      }
    }
  };

  const handleImageUpload = async (file) => {
    setUploadingImage(true);
    try {
      toast.loading('Загрузка изображения...', { id: 'uploadImage' });
      const response = await uploadHomeworkImage(file);
      if (response && response.success) {
        setUploadedFile({ url: response.data.url, filename: response.data.filename });
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
        setFormData(prev => ({ ...prev, solutionImage: response.data.url }));
        toast.success('Изображение загружено', { id: 'uploadImage' });
      } else {
        toast.error('Ошибка загрузки изображения', { id: 'uploadImage' });
      }
    } catch (error) {
      toast.error('Ошибка загрузки изображения: ' + (error.message || ''), { id: 'uploadImage' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    if (!uploadedFile && !formData.solutionImage) return;
    if (await confirmDialog({ title: 'Удалить изображение?', message: 'Изображение решения будет удалено.', confirmText: 'Удалить' })) {
      try {
        if (uploadedFile) {
          toast.loading('Удаление изображения...', { id: 'deleteImage' });
          await deleteHomeworkImage(uploadedFile.filename);
          setUploadedFile(null); setImagePreview('');
          setFormData(prev => ({ ...prev, solutionImage: '' }));
          toast.success('Изображение удалено', { id: 'deleteImage' });
        }
        if (editingHomework && formData.solutionImage && !uploadedFile) {
          setFormData(prev => ({ ...prev, solutionImage: '' }));
          toast.success('Изображение будет удалено при сохранении');
        }
      } catch (error) {
        console.error('Error deleting image:', error);
        toast.error('Ошибка удаления изображения', { id: 'deleteImage' });
      }
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Файл слишком большой. Максимальный размер 5MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Пожалуйста, выберите изображение'); return; }
    handleImageUpload(file);
  };

  const handleSubmit = async () => {
    if (!formData.description) { toast.error('Введите текст задания'); return; }
    if (!selectedLesson) { toast.error('Выберите урок'); return; }
    try {
      const finalSolutionImage = formData.solutionImage || (uploadedFile ? uploadedFile.url : '');
      const serializedAnswer = formData.answerType === 'grid' ? JSON.stringify(gridAnswers.flat()) : formData.correctAnswer;
      const homeworkData = {
        title: formData.title, description: formData.description,
        correctAnswer: serializedAnswer,
        answerType: formData.answerType, gridRows: formData.gridRows, gridCols: formData.gridCols,
        textSolution: formData.textSolution, videoSolution: formData.videoSolution,
        solutionImage: finalSolutionImage,
        attachedFiles, attachedSolutionFiles
      };
      if (editingHomework) {
        await updateHomework(editingHomework.id, homeworkData);
        toast.success('Задание обновлено');
      } else {
        await createHomework({ ...homeworkData, lessonId: selectedLesson });
        toast.success('Задание создано');
      }
      if (quickAddMode) {
        const nextNumber = getNextHomeworkNumber(selectedLesson) + 1;
        setFormData({ title: `№${nextNumber}`, description: '', correctAnswer: '', answerType: 'single', gridRows: 3, gridCols: 2, textSolution: '', solutionImage: '', videoSolution: '' });
        setGridAnswers(buildEmptyGrid(3, 2));
        setAttachedFiles([]); setAttachedSolutionFiles([]);
        setUploadedFile(null); setImagePreview('');
        setFormKey(k => k + 1);
      } else {
        setModalOpen(false);
      }
      loadData();
    } catch (error) {
      console.error('ERROR в handleSubmit:', error);
      toast.error('Ошибка при сохранении');
    }
  };

  const handleReview = async (answerId, status) => {
    try {
      await reviewAnswer(answerId, status);
      toast.success('Ответ проверен');
      setAnswerModalOpen(false);
      loadData();
    } catch {
      toast.error('Ошибка при проверке');
    }
  };

  // Название курса по id — из реального списка курсов (а не захардкожено)
  const courseNameById = (cid) => courses.find(c => c.id === cid)?.name || 'Без курса';

  if (loading) return <Loader />;

  const treeExp = {
    courses: expandedCourses, sections: expandedSections, lessons: expandedLessons,
    toggleCourse, toggleSection, toggleLesson,
  };

  return (
    <div style={{ maxWidth: 1180 }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: embedded ? 'flex-end' : 'space-between', marginBottom: 24 }}>
        {!embedded && (
          <div>
            <h1 className="s-page-title">Задания</h1>
            <p className="s-page-sub">Домашние задания по урокам</p>
          </div>
        )}
        <button onClick={handleAdd} className="action-btn purple">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Новое задание
        </button>
      </div>

      {/* Дерево курс → раздел → урок → номера ДЗ с перетаскиванием */}
      <HomeworkTree
        homeworks={homeworks}
        courses={courses}
        exp={treeExp}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onQuickAdd={handleQuickAdd}
      />

      {/* Add/Edit homework Modal — дизайн «Доработки» */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} bare>
        <div className="adm-modal w-hw">
          <div className="adm-modal-head">
            <div className="adm-head-ic">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
            </div>
            <div className="adm-head-tt">
              <div className="adm-modal-title">{editingHomework ? 'Редактировать задание' : (quickAddMode ? 'Быстрое добавление задания' : 'Новое задание')}</div>
              <div className="adm-modal-desc">{formData.title || 'Задание'}{selectedCourse ? ` · ${courseNameById(selectedCourse)}` : ''}</div>
            </div>
            <button className="adm-x" aria-label="Закрыть" onClick={() => setModalOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div className="adm-modal-body">
            <div className="fl-grid2">
              <div className="fl">
                <label className="fl-label">Курс</label>
                <Dropdown value={selectedCourse} disabled={!!editingHomework || quickAddMode}
                  options={courses.map(c => ({ value: c.id, label: c.name }))}
                  onChange={(v) => { setSelectedCourse(parseInt(v)); setSelectedSection(''); setSelectedLesson(''); }} />
              </div>
              <div className="fl">
                <label className="fl-label">Раздел</label>
                <Dropdown value={selectedSection} placeholder="Выберите раздел" disabled={!selectedCourse || !!editingHomework || quickAddMode}
                  options={sections.map(s => ({ value: s.id, label: s.name }))}
                  onChange={(v) => { setSelectedSection(v); setSelectedLesson(''); }} />
              </div>
            </div>

            <div className="fl">
              <label className="fl-label">Урок</label>
              <Dropdown value={selectedLesson} placeholder="Выберите урок" disabled={!selectedSection || !!editingHomework}
                options={lessons.map(l => ({ value: l.id, label: l.title }))}
                onChange={(v) => setSelectedLesson(v)} />
            </div>

            <div className="fl">
              <label className="fl-label">Заголовок задания</label>
              <input className="fl-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
            </div>

            <div className="fl">
              <div className="fl-rowhead">
                <label className="fl-label">Текст задания</label>
                <button type="button" className="add-file-btn2" onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.onchange = async (e) => { const f = e.target.files[0]; if (f) await handleAddFile(f, 'homework'); }; inp.click(); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  Файл
                </button>
              </div>
              <RichEditor
                key={formKey}
                value={formData.description}
                onChange={(v) => setFormData(fd => ({ ...fd, description: v }))}
                placeholder=""
                onImageUpload={async (file) => { try { toast.loading('Загрузка изображения...', { id: 'imgup' }); const r = await uploadHomeworkImage(file); if (r && r.success) { toast.success('Изображение добавлено', { id: 'imgup' }); return r.data.url; } toast.dismiss('imgup'); } catch { toast.error('Ошибка загрузки', { id: 'imgup' }); } return null; }}
              />
              {attachedFiles.length > 0 && (
                <div className="mat-list2" style={{ marginTop: 8 }}>
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="mat-item2">
                      <span className="mat-ic2" style={{ background: fileColor(file.name)[0], color: fileColor(file.name)[1] }}><FileIcon name={file.name} size={18} /></span>
                      <a className="mat-name2" href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-1)', textDecoration: 'none' }}>{file.name}</a>
                      {fmtSize(file.size) && <span className="mat-size2">{fmtSize(file.size)}</span>}
                      <button className="mat-del2" aria-label="Удалить" onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="fl-grid2">
              <div className="fl">
                <label className="fl-label">Тип ответа</label>
                <div className="seg-toggle">
                  {[{ key: 'single', label: 'Одно значение' }, { key: 'grid', label: 'Сетка' }].map(opt => (
                    <button key={opt.key} type="button" className={formData.answerType === opt.key ? 'active' : ''}
                      onClick={() => { setFormData(fd => ({ ...fd, answerType: opt.key, correctAnswer: '' })); setGridAnswers(buildEmptyGrid(formData.gridRows, formData.gridCols)); }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {formData.answerType === 'single' && (
                <div className="fl">
                  <label className="fl-label">Правильный ответ</label>
                  <input className="fl-input" value={formData.correctAnswer} onChange={(e) => setFormData({ ...formData, correctAnswer: e.target.value })} />
                </div>
              )}
            </div>

            {formData.answerType === 'grid' && (
              <div className="fl">
                <label className="fl-label">Сетка ответов</label>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'center' }}>
                  <label style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>Строки
                    <input type="number" min="1" max="20" value={formData.gridRows} onChange={e => { const r = Math.max(1, Math.min(20, +e.target.value || 1)); setFormData(fd => ({ ...fd, gridRows: r })); setGridAnswers(prev => rebuildGrid(r, formData.gridCols, prev)); }} style={{ width: 64, padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--card)', color: 'var(--text-1)' }} />
                  </label>
                  <label style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>Столбцы
                    <input type="number" min="1" max="6" value={formData.gridCols} onChange={e => { const c = Math.max(1, Math.min(6, +e.target.value || 1)); setFormData(fd => ({ ...fd, gridCols: c })); setGridAnswers(prev => rebuildGrid(formData.gridRows, c, prev)); }} style={{ width: 64, padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--card)', color: 'var(--text-1)' }} />
                  </label>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>
                  Можно вставить все ответы сразу: скопируйте их через пробел или с переносами строк и вставьте в первую ячейку — они разложатся по сетке.
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <tbody>
                      {Array.from({ length: formData.gridRows }, (_, r) => (
                        <tr key={r}>
                          {Array.from({ length: formData.gridCols }, (_, c) => (
                            <td key={c} style={{ padding: 3, border: '1px solid var(--border)' }}>
                              <input type="text" value={gridAnswers[r]?.[c] ?? ''} onChange={e => { const val = e.target.value; setGridAnswers(prev => { const next = prev.map(row => [...row]); if (!next[r]) next[r] = Array(formData.gridCols).fill(''); next[r][c] = val; return next; }); }}
                                onPaste={e => {
                                  const tokens = e.clipboardData.getData('text').split(/\s+/).filter(Boolean);
                                  if (tokens.length <= 1) return; // обычная вставка одного значения — не мешаем
                                  e.preventDefault();
                                  const rows = formData.gridRows, cols = formData.gridCols;
                                  setGridAnswers(prev => {
                                    const next = Array.from({ length: rows }, (_, rr) => Array.from({ length: cols }, (_, cc) => prev[rr]?.[cc] ?? ''));
                                    let idx = 0;
                                    for (let p = r * cols + c; p < rows * cols && idx < tokens.length; p++, idx++) {
                                      next[Math.floor(p / cols)][p % cols] = tokens[idx];
                                    }
                                    return next;
                                  });
                                }}
                                placeholder={`[${r+1},${c+1}]`} style={{ width: '100%', padding: '7px 9px', border: 'none', background: 'rgba(124,77,255,0.05)', fontSize: 13, outline: 'none', minWidth: 80, boxSizing: 'border-box', color: 'var(--text-1)' }} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="fl-section"><span className="ln" /><span className="tx">Решение</span><span className="ln" /></div>

            <div className="fl">
              <div className="fl-rowhead">
                <label className="fl-label">Текстовое решение</label>
                <button type="button" className="add-file-btn2" onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.onchange = async (e) => { const f = e.target.files[0]; if (f) await handleAddFile(f, 'solution'); }; inp.click(); }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  Файл
                </button>
              </div>
              <RichEditor
                value={formData.textSolution}
                onChange={(v) => setFormData(fd => ({ ...fd, textSolution: v }))}
                placeholder=""
                onImageUpload={async (file) => { try { toast.loading('Загрузка изображения...', { id: 'imgup' }); const r = await uploadHomeworkImage(file); if (r && r.success) { toast.success('Изображение добавлено', { id: 'imgup' }); return r.data.url; } toast.dismiss('imgup'); } catch { toast.error('Ошибка загрузки', { id: 'imgup' }); } return null; }}
              />
              {attachedSolutionFiles.length > 0 && (
                <div className="mat-list2" style={{ marginTop: 8 }}>
                  {attachedSolutionFiles.map((file, index) => (
                    <div key={index} className="mat-item2">
                      <span className="mat-ic2" style={{ background: fileColor(file.name)[0], color: fileColor(file.name)[1] }}><FileIcon name={file.name} size={18} /></span>
                      <a className="mat-name2" href={file.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-1)', textDecoration: 'none' }}>{file.name}</a>
                      {fmtSize(file.size) && <span className="mat-size2">{fmtSize(file.size)}</span>}
                      <button className="mat-del2" aria-label="Удалить" onClick={() => setAttachedSolutionFiles(prev => prev.filter((_, i) => i !== index))}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="fl">
              <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg></span>Ссылка на видеорешение <span className="opt">— необязательно</span></label>
              <div className="fl-iwrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                <input className="fl-input with-ic" value={formData.videoSolution} onChange={(e) => setFormData({ ...formData, videoSolution: e.target.value })} />
              </div>
            </div>

            <div className="fl">
              <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>Изображение решения</label>
              <div className="file-pick">
                <button type="button" className="fp-btn" onClick={() => document.getElementById('solImgInput').click()} disabled={uploadingImage}>{uploadingImage ? 'Загрузка...' : 'Выбрать файл'}</button>
                <span className="fp-name">{(imagePreview || formData.solutionImage) ? 'файл выбран' : 'файл не выбран'}</span>
                <input id="solImgInput" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} disabled={uploadingImage} />
              </div>
              {(imagePreview || formData.solutionImage) && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <img src={imagePreview || formData.solutionImage} alt="Решение" style={{ maxWidth: 150, borderRadius: 10, border: '1px solid var(--border)' }} />
                  <button type="button" className="adm-btn adm-btn-sec" style={{ padding: '8px 14px' }} onClick={handleImageDelete}>Удалить</button>
                </div>
              )}
            </div>
          </div>

          <div className="adm-modal-foot">
            {quickAddMode && <span className="adm-foot-hint"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>Форма очистится для следующего задания</span>}
            <button className="adm-btn adm-btn-sec" onClick={() => setModalOpen(false)}>{quickAddMode ? 'Завершить' : 'Отмена'}</button>
            <button className="adm-btn adm-btn-pri" onClick={handleSubmit} disabled={uploadingImage}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              {uploadingImage ? 'Загрузка...' : (quickAddMode ? 'Добавить' : (editingHomework ? 'Сохранить' : 'Создать'))}
            </button>
          </div>
        </div>
      </Modal>

      {/* Answer review Modal */}
      <Modal isOpen={answerModalOpen} onClose={() => setAnswerModalOpen(false)} title="Проверка домашнего задания">
        {selectedAnswer && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <p><strong>Ученик:</strong> {selectedAnswer.User?.name}</p>
              <p><strong>Email:</strong> {selectedAnswer.User?.email}</p>
              <p><strong>Курс:</strong> {courseNameById(selectedAnswer.Homework?.Lesson?.Section?.courseId)}</p>
              <p><strong>Раздел:</strong> {selectedAnswer.Homework?.Lesson?.Section?.name}</p>
              <p><strong>Урок:</strong> {selectedAnswer.Homework?.Lesson?.title}</p>
              <p><strong>Задание:</strong></p>
              <div dangerouslySetInnerHTML={{ __html: selectedAnswer.Homework?.description }} />
            </div>
            <div style={{ background: '#f8fafd', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              <p><strong>Ответ ученика:</strong></p>
              {selectedAnswer.answerImage
                ? <img src={selectedAnswer.answerImage} alt="Ответ" style={{ maxWidth: '100%', borderRadius: '12px' }} />
                : <p style={{ background: 'var(--card)', padding: '12px', borderRadius: '8px' }}>{selectedAnswer.answer}</p>
              }
            </div>
            {selectedAnswer.Homework?.correctAnswer && (
              <div style={{ background: '#e3f6e9', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <p><strong>Правильный ответ:</strong> {selectedAnswer.Homework.correctAnswer}</p>
              </div>
            )}
            {(selectedAnswer.Homework?.textSolution || selectedAnswer.Homework?.videoSolution || selectedAnswer.Homework?.solutionImage) && (
              <div style={{ background: '#f0f2f5', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <p style={{ fontWeight: 600, marginBottom: '8px' }}>Решение от учителя:</p>
                {selectedAnswer.Homework?.textSolution && <div dangerouslySetInnerHTML={{ __html: selectedAnswer.Homework.textSolution }} />}
                {selectedAnswer.Homework?.videoSolution && (
                  <div style={{ marginBottom: '8px' }}>
                    <a href={selectedAnswer.Homework.videoSolution} target="_blank" rel="noopener noreferrer">Видеорешение</a>
                  </div>
                )}
                {selectedAnswer.Homework?.solutionImage && (
                  <img src={selectedAnswer.Homework.solutionImage} alt="Решение учителя" style={{ maxWidth: '200px', borderRadius: '8px', cursor: 'pointer' }} onClick={() => window.open(selectedAnswer.Homework.solutionImage, '_blank')} />
                )}
              </div>
            )}
            <div className="form-actions">
              <Button variant="danger" onClick={() => handleReview(selectedAnswer.id, 'incorrect')}>Неверно</Button>
              <Button variant="purple" onClick={() => handleReview(selectedAnswer.id, 'correct')}>Верно</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default HomeworkEditor;
